import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    ViewContainerRef,
    OnInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController, StateService, Tracker, ICollapsibleMenuItem, ISecondaryMenuItem } from 'flux-core';
import { AbstractCollapsibleMenuItem } from 'flux-core/src/ui';
import { PlanPermManager, UpgradeDialogType, UpgradeDialogWindow } from 'flux-user';
import { BehaviorSubject, Observable, merge, Subscription } from 'rxjs';
import { tap, filter } from 'rxjs/operators';
import { IDialogBoxData, PlanPermission } from 'flux-definition';
import { TempAddLibsMenu } from '../temp-add-libs-menu.cmp';
import { LibraryList } from '../library-list';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'libs-deepview',
    templateUrl: './libs-deepview-item.cmp.html',
    styleUrls: [ './libs-deepview-item.scss' ],
})
export class LibsDeepviewItem extends AbstractCollapsibleMenuItem implements OnInit, OnDestroy {

    /**
     * Event emitter to emit when click event fires on
     * the header element.
     */
    @Output()
    public toggleChild: EventEmitter<any>;

    /**
     * Event emitter to emit when click event fires on
     * remove button.
     */
    @Output()
    public removeChild: EventEmitter<any>;

    /**
     * Event emitter to emit when the title is edited
     */
    @Output()
    public titleChanged: EventEmitter<any>;

    /**
     * If true enable close button to remove the component from parent panel
     */
    @Input()
    public removable: boolean = true;

    /**
     * Determine whether menu item is collapsable
     */
    @Input()
    public collapsable: boolean = true;

    /**
     * If true enable secondary menu.
     */
    @Input()
    public secondaryMenu: boolean = false;

    /**
     * Secondary menu interface.
     */
    @Input()
    public items: ISecondaryMenuItem[] = [];

    @Input()
    public isPremium: boolean = false;


    public checked: BehaviorSubject<boolean> = new BehaviorSubject( false );

    /**
     * This holds the instance of the component panel that is rendered on the container child
     */
    public menuItem: ICollapsibleMenuItem;

    /**
     * Whether the user is in free or demo plan or not
     */
    public isFreeOrDemoUser: Observable<boolean>;

    /**
     * Whether the user is in lite plan or not
     */
    public isLitePlanUser: Observable<boolean>;

    public isFreeUser: boolean = false;

    /**
     * Array with all the subs.
     */
    protected subs: Array<Subscription>;

    /**
     * Expands the item.
     */
    @ViewChild( 'componentContainer', { read: ViewContainerRef, static: true })
    protected componentContainer: ViewContainerRef;

    constructor(
        protected componentFactoryResolver: ComponentFactoryResolver,
        protected injector: Injector,
        protected cdr: ChangeDetectorRef,
        protected modalController: ModalController,
        protected planPermManager: PlanPermManager,
        public translate: TranslateService,
        protected state: StateService<any, any>,
        protected tempAddLibsMenu: TempAddLibsMenu,
        protected libraryList: LibraryList,
    ) {
        super( componentFactoryResolver, injector, cdr, translate );
        this.toggleChild = new EventEmitter();
        this.removeChild = new EventEmitter();
        this.titleChanged = new EventEmitter();

        this.subs = [];
        this.isFreeOrDemoUser = this.planPermManager.isFreeOrDemoUser();
        this.isLitePlanUser = this.planPermManager.isLiteUser();
    }

    /**
     * Title to be shown on header
     */
    public get title(): string {
        if ( this.menuItem ) {
            return this.menuItem.title;
        }
    }

    /**
     * Secondary text to be shown on header
     */
    public get subText(): Observable<string> {
        if ( this.menuItem ) {
            return this.menuItem.subText;
        }
    }

    public get options(): any {
        if ( this.menuItem ) {
            return this.menuItem.options;
        }
    }

    /**
     * Id of the compoenent
     */
    @Input()
    public get id(): string {
        return this.menuItem.id;
    }

    public ngOnInit(): void {
        const sub1 = this.state.changes( 'CurrentLibrariesClone' ).pipe(
            tap( selectedLibraries => {
                if ( selectedLibraries.some( i => i.id === this.id )) {
                    this.checked.next( true );
                } else {
                    this.checked.next( false );
                }
            }),
        ).subscribe();

        const sub2 = merge(
            this.planPermManager.isFreeOrDemoUser(),
            this.planPermManager.isLiteUser(),
        ).pipe(
            filter( value => !!value ),
            tap( value => this.isFreeUser = value ),
        ).subscribe();

        this.subs.push( sub1, sub2 );
    }

    /**
     * Creates an instance of an menu item and append it to the end of the menu
     * @param type  The type of the component to create.This should be a ICollapsibleMenuItem Component class.
     * @param inputs    An object with all the inputs that should be set to the
     *      type component instance. Object keys should have the same name as the input field.
     */
    public add( type: any, inputs?: { [inputName: string]: any }) {
        const menuItemCmpRef: ComponentRef<any> = this.createComponent( type, this.componentContainer );
        this.menuItem = menuItemCmpRef.instance;
        inputs = { ...inputs, subText: this.subText, source: 'lib-panel-deepview' };
        this.setInputs( this.menuItem, inputs );
    }

    /**
     * Open the upgrade window
     */
    public openUpgradeWindow(): void {

        // Adding tracker for premium button click
        Tracker.track( 'conversion.tag.premium.click', {
            value1: this.menuItem.id, value1Type: 'feature',
            value2: 'shapes-panel', value2Type: 'location',
        });

        const dialogData = {
            id: PlanPermission.CREATELY_PREMIUM_LIBRARIES,
            iframeType: UpgradeDialogType.PremiumShapelibraries,
            buttons: [
                {
                    type: 'upgrade',
                    clickHandler: () => {},
                },
            ],
            integrationContext: {
                embedded: this.state.get( 'ApplicationIsEmbedded' ),
                environment: this.state.get( 'PluginApp' ),
            },
        } as IDialogBoxData;

        this.modalController.show( UpgradeDialogWindow, {
            inputs: {
                dialogData,
            },
        });
    }

    public handleSelection( event ) {
        const libId = this.id;
        const selected = [];
        const checked = event.target.checked;
        // if ( checked && this.isFreeUser && this.tempAddLibsMenu.isPremiumLibrary( libId )) {
        //     return this.tempAddLibsMenu.openUpgradeDialog();
        // }
        this.state.get( 'CurrentLibrariesClone' ).forEach( lib => selected.push( lib.id ));
        if ( checked ) {
            selected.push( libId );
            this.tempAddLibsMenu.onSelectionChange( selected );
        } else {
            const newSelected = selected.filter( i => i !== libId );
            this.tempAddLibsMenu.onSelectionChange( newSelected );
        }
        const { definition, group } = this.libraryList.getLibraryDeepView( libId );
        if ( group ) {
            this.tempAddLibsMenu.updateLibraryData( definition.id, libId, checked, group );
        } else {
            this.tempAddLibsMenu.updateLibraryData( definition.id, libId, checked );
        }
    }

    /**
     * Unsubscribes from all subscriptions.
     */
    public ngOnDestroy() {
        while ( this.subs.length > 0 ) {
            this.subs.pop().unsubscribe();
        }
        this.toggleChild = null;
        this.removeChild = null;
        this.titleChanged = null;
        this.menuItem = null;
        this._parent = null;
        this._isOpen = false;
    }
}
